/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import cx from 'classnames';
import { get, isEmpty } from 'lodash';

import AssetPreview from '../../../common/components/AssetPreview';
import Dropdown from '@broadleaf/admin-components/dist/common/components/Dropdown';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { fragment } from '@broadleaf/admin-components/dist/common/utils/path';

import messages from './ThumbnailTile.messages';

/**
 * Component used for displaying a thumbnail preview with optional actions. It
 * takes in optional keys for mapping item properties to display information.
 * ThumnailTiles are selectable
 */
export const ThumbnailTile: React.FC<ThumbnailTileProps> = props => {
  const formatMessage = useFormatMessage();
  const alt = get(props.item, props.altKey);
  const embedCode = get(props.item, props.embedCodeKey);
  const isPrimary = get(props.item, props.primaryKey, false);
  const src = get(props.item, props.srcKey);
  const tags = get(props.item, props.tagsKey, []);
  const title = get(
    props.item,
    props.titleKey,
    src ? fragment(src, -1, false) : formatMessage(messages.emptyTitle)
  );
  const type = get(props.item, props.typeKey);
  return (
    <div
      className={cx(
        'ThumbnailTile tw-relative tw-w-40 tw-cursor-pointer tw-rounded-sm tw-border tw-border-solid tw-bg-white tw-bg-clip-border tw-shadow hover:tw-block',
        {
          'ThumbnailTile--selected before:tw-pointer-events-none before:tw-absolute before:tw-inset-[-3px] before:tw-z-[2] before:tw-rounded before:tw-border-2 before:tw-border-solid before:tw-border-green-600':
            props.selected
        },
        props.className
      )}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        props.onClick(props.item);
      }}
    >
      {isPrimary && (
        <div className="ThumbnailTile__primary tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-text-base">
          <i className="ThumbnailTile__primaryIcon fa fa-star tw-block tw-text-yellow-600" />
        </div>
      )}
      <AssetPreview
        alt={alt}
        className="ThumbnailTile__preview tw-h-40 tw-w-full tw-rounded-none tw-rounded-t-sm"
        embedCode={embedCode}
        src={src}
        type={type}
      />
      {!!props.actions && <ThumbnailTileDropdown actions={props.actions} />}
      <div className="ThumbnailTile__details">
        <div className="ThumbnailTile__title tw-h-9 tw-truncate tw-border-t tw-border-solid tw-border-t-gray-200 tw-p-2 tw-text-xs tw-font-bold tw-text-gray-700">
          {title}
        </div>
        <div className="ThumbnailTile__tagsWrapper tw-h-8 tw-border-t tw-border-solid tw-border-t-gray-200 tw-p-1">
          <div className="ThumbnailTile__tags tw-flex tw-h-full tw-truncate">
            {!isEmpty(tags) ? (
              tags.map(tag => (
                <div
                  className="ThumbnailTile__tag tw-mx-0.5 tw-my-0 tw-inline tw-rounded-md tw-bg-green-600 tw-p-1 tw-text-xs tw-text-white"
                  key={tag}
                >
                  {tag}
                </div>
              ))
            ) : (
              <div className="ThumbnailTile__emptyTag tw-inline tw-p-1 tw-text-xs tw-text-gray-300">
                {formatMessage(messages.emptyTag)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ThumbnailTile.defaultProps = {
  altKey: 'alt',
  embedCodeKey: 'embedCode',
  primaryKey: 'primary',
  srcKey: 'url',
  tagsKey: 'tags',
  titleKey: 'title',
  typeKey: 'type'
};

export interface ThumbnailTileProps {
  /**
   * An array of action items to be rendered by the dropdown. Each item provides
   * the text to render, an onClick function, any classNames, and may be
   * disabled
   */
  actions?: Array<{
    text?: string;
    onClick?: Function;
    classNames?: string;
    disabled?: boolean;
  }>;
  /** the key of the alt text value on the item object */
  altKey?: string;
  /** Optional className for the tile component. */
  className?: string;
  /**
   * The property key for getting an item's embed code value. This is used for item's without a source url and instead contain embedded content.
   */
  embedCodeKey?: string;
  /** the item which you are displaying a thumbnail for */
  item: object;
  /** A function which is called when the thumbnail tile is clicked */
  onClick?: Function;
  /** The property key used to get whether or not this is a primary item and should render a star. */
  primaryKey?: string;
  /** A boolean controlling if the highlighting is displayed on this thumbnail */
  selected?: boolean;
  /** the key of the src on the item object */
  srcKey?: string;
  /** the key of the tags on the item object */
  tagsKey?: string;
  /** the key of the title on the item object */
  titleKey?: string;
  /** the key of the type on the item object */
  typeKey?: string;
}

export const ThumbnailTileDropdown = ({ actions }) => (
  <div className="ThumbnailTileActions tw-absolute tw-right-1 tw-top-0.5 tw-hidden hover:tw-text-gray-800">
    <Dropdown>
      <Dropdown.Menu.Trigger flat>
        <i className="fa fa-ellipsis-h" />
      </Dropdown.Menu.Trigger>
      <Dropdown.Menu>
        {actions.map((action, i) => (
          <Dropdown.Menu.Item key={i} onClick={action.onClick}>
            {action.text}
          </Dropdown.Menu.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  </div>
);

export default ThumbnailTile;
